/**
 * Private router-state handoff from the MFA splash's Step-1 idea field into
 * the starter wizard (owner spec 2026-07-26). The idea travels ONLY via
 * React Router `location.state` — never the URL, storage, or an API call —
 * so a kid's idea is not exposed in referrers, history params, or logs.
 *
 * Consumers: MfaSplashPage builds it, AuthGate defaults to Create Account
 * when it is present, StarterWizardPage consumes it (idea + step 2) and
 * clears the history state.
 */

export const STARTER_HANDOFF_KIND = "mfa-splash-starter";
export const STARTER_IDEA_MAX_LENGTH = 1000;

export interface MfaStarterHandoffV1 {
  kind: typeof STARTER_HANDOFF_KIND;
  version: 1;
  idea: string;
}

/** Null when the idea trims to empty or exceeds the canonical bound. */
export function buildStarterHandoff(idea: string): MfaStarterHandoffV1 | null {
  const trimmed = idea.trim();
  if (!trimmed || trimmed.length > STARTER_IDEA_MAX_LENGTH) return null;
  return { kind: STARTER_HANDOFF_KIND, version: 1, idea: trimmed };
}

/**
 * Returns the trimmed canonical idea, or null for anything that is not a
 * well-formed v1 handoff (missing, foreign state, wrong kind/version,
 * non-string, empty, or oversized). Callers fall back to the normal
 * empty-Step-1 wizard on null — invalid state can never skip Step 1.
 */
export function parseStarterHandoff(state: unknown): string | null {
  if (typeof state !== "object" || state === null) return null;
  const candidate = state as Record<string, unknown>;
  if (candidate.kind !== STARTER_HANDOFF_KIND || candidate.version !== 1) return null;
  if (typeof candidate.idea !== "string") return null;
  const idea = candidate.idea.trim();
  if (!idea || idea.length > STARTER_IDEA_MAX_LENGTH) return null;
  return idea;
}
